import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { catchError, map, Observable, of, OperatorFunction, Subscription, switchMap, throwError } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { ConfigService, SentryLogger } from '@fcom/core/services';
import { DEFAULT_RETRY_BACKOFF_TIME, retryWithBackoff, STATUS_CODES_FOR_RETRY } from '@fcom/rx';
import { mapErrorForSentry, unsubscribe } from '@fcom/core';
import {
  CheckInAcceptCancelRequest,
  CheckInBoardingPassByEmailRequest,
  CheckInBoardingPassByEmailResponse,
  CheckInInitializeRequest,
  CheckInReassignExitSeatsRequest,
  FinnairAdvancePassengerInformationRequest,
  FinnairAdvancePassengerInformationRequestV2,
  FinnairEmergencyContactUpdateRequest,
  FinnairOrder,
  FinnairPassengerRequest,
  FinnairServiceRequestItem,
  RequestInitiator,
  TourOperatorCode,
} from '@fcom/dapi/api/models';
import { isNull } from '@fcom/core/utils';
import { DapiHttpResponse } from '@fcom/dapi/api/dapi-http-response';
import { getDapiErrorKey } from '@fcom/common-booking/utils/common-booking.utils';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import {
  acceptCheckIn,
  cancelCheckIn,
  fetchOrder,
  getOrderByPnrAndLastName,
  getOrderByTourOperatorAndLastName,
  initializeCheckIn,
  reassignExitSeats,
  sendBoardingPass,
  setServices,
  updateAdvancePassengerInformation,
  updateApis,
  updateEmergencyContactDetails,
  updatePassengerDetails,
} from '@fcom/dapi/api/order-functions';

const ORDER_STATUS_CODES_FOR_RETRY = [503, ...STATUS_CODES_FOR_RETRY];

@Injectable({ providedIn: 'root' })
export class MmbOrderService implements OnDestroy {
  private http = inject(HttpClient);
  private dapiService = inject(DapiService);
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);
  private sentryLogger = inject(SentryLogger);

  static NUMBER_OF_RETRIES = 1;
  static readonly LOCATION_HEADER = 'Location';

  private subscriptions = new Subscription();

  private readonly CATCH_ERROR: (
    isTourOperator?: boolean,
    trackingCallback?: (errorKey: string) => void
  ) => OperatorFunction<unknown, unknown> = (isTourOperator = false) =>
    catchError((err: unknown) => {
      if (!getDapiErrorKey(err).startsWith('ORDER_')) {
        this.logErrorToSentry(err, getDapiErrorKey(err), isTourOperator);
      }

      return throwError(() => {
        return err;
      });
    });

  logErrorToSentry(err: unknown, errorKey: string, isTourOperator = false): void {
    this.sentryLogger.error(`Error fetching order ${isTourOperator ? 'for tour operator ' : ''}${errorKey}`, {
      error: mapErrorForSentry(err),
    });
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  private locationFromResponse = (response: DapiHttpResponse<void>) =>
    response.headers.get(MmbOrderService.LOCATION_HEADER);

  /**
   * load Order by PNR and surname
   *
   * @param familyName surname for the PNR
   * @param bookingRef bookingRef for the PNR to fetch
   * @param requestInitiator requestInitiator to define where we started the call
   * @param firstName to be used for group bookings
   */
  loadOrder(
    familyName: string,
    bookingRef: string,
    requestInitiator: RequestInitiator,
    firstName?: string
  ): Observable<FinnairOrder> {
    return this.dapiService
      .invokeResponse$(getOrderByPnrAndLastName, this.configService.cfg.orderUrl, {
        body: {
          id: bookingRef.toUpperCase(),
          lastName: familyName,
          locale: this.languageService.localeValue,
          requestInitiator,
          ...(firstName ? { firstName } : {}),
        },
      })
      .pipe(
        this.retryGet$(),
        this.CATCH_ERROR(),
        switchMap((orderLocation: string) => this.getOrderByUrl(orderLocation))
      );
  }

  /**
   * load Order by booking reference, surname and tour operator code
   *
   * @param familyName surname for the PNR
   * @param bookingRef bookingRef for the PNR to fetch
   * @param tourOperatorCode tourOperatorCode for the PNR to fetch
   */
  loadOrderTourOperator(
    familyName: string,
    bookingRef: string,
    tourOperatorCode: TourOperatorCode
  ): Observable<FinnairOrder> {
    return this.dapiService
      .invokeResponse$(getOrderByTourOperatorAndLastName, this.configService.cfg.orderUrl, {
        body: {
          id: bookingRef.toUpperCase(),
          lastName: familyName,
          locale: this.languageService.localeValue,
          requestInitiator: RequestInitiator.MMB,
          tourOperatorCode,
        },
      })
      .pipe(
        this.retryGet$(),
        this.CATCH_ERROR(true),
        switchMap((orderLocation: string) => this.getOrderByUrl(orderLocation))
      );
  }

  completeCheckIn(orderId: string, hash: string, body: CheckInAcceptCancelRequest): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(acceptCheckIn, this.configService.cfg.orderUrl, {
        orderId,
        hash,
        body,
      })
    );
  }

  reassignExitRowSeats(orderId: string, hash: string, body: CheckInReassignExitSeatsRequest): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(reassignExitSeats, this.configService.cfg.orderUrl, {
        orderId,
        hash,
        body,
      })
    );
  }

  sendBoardingPass(
    orderId: string,
    hash: string,
    body: CheckInBoardingPassByEmailRequest
  ): Observable<CheckInBoardingPassByEmailResponse> {
    return this.dapiService.invoke$(sendBoardingPass, this.configService.cfg.orderUrl, {
      orderId,
      hash,
      body,
    });
  }

  cancelCheckIn(orderId: string, hash: string, body: CheckInAcceptCancelRequest): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(cancelCheckIn, this.configService.cfg.orderUrl, {
        orderId,
        hash,
        body,
      })
    );
  }

  setServices$(orderId: string, hash: string, serviceRequests: FinnairServiceRequestItem[]): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(setServices, this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        body: serviceRequests,
      })
    );
  }

  updateEmergencyContactDetails$(
    orderId: string,
    hash: string,
    serviceRequest: FinnairEmergencyContactUpdateRequest
  ): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(updateEmergencyContactDetails, this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        body: serviceRequest,
      })
    );
  }

  updateApis(
    orderId: string,
    hash: string,
    serviceRequest: FinnairAdvancePassengerInformationRequest[]
  ): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(updateAdvancePassengerInformation, this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        body: serviceRequest,
      })
    );
  }

  updateRegulatoryDetails(
    orderId: string,
    hash: string,
    serviceRequest: FinnairAdvancePassengerInformationRequestV2[]
  ): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(updateApis, this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        body: serviceRequest,
      })
    );
  }

  updateContactDetails(
    orderId: string,
    hash: string,
    serviceRequest: FinnairPassengerRequest
  ): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(updatePassengerDetails, this.configService.cfg.orderUrl, {
        orderId,
        hash,
        locale: this.languageService.localeValue,
        body: serviceRequest,
      })
    );
  }

  initializeCheckIn(orderId: string, hash: string, request: CheckInInitializeRequest): void {
    this.subscriptions.add(
      this.dapiService
        .invokeResponse$(initializeCheckIn, this.configService.cfg.orderUrl, {
          orderId: orderId,
          hash: hash,
          body: request,
        })
        .subscribe({
          error: (err: unknown) =>
            this.sentryLogger.error('Check-in initialize call failed with ' + getDapiErrorKey(err), {
              error: mapErrorForSentry(err),
            }),
        })
    );
  }

  fetchOrderAfterRequest(request$: Observable<DapiHttpResponse<void>>): Observable<FinnairOrder> {
    return request$.pipe(
      map((res) => this.locationFromResponse(res)),
      retryWithBackoff(MmbOrderService.NUMBER_OF_RETRIES, DEFAULT_RETRY_BACKOFF_TIME, [503, ...STATUS_CODES_FOR_RETRY]),
      switchMap((location: string) => this.getOrderByUrl(location))
    );
  }

  getOrderByIdAndHash(orderId: string, hash: string): Observable<FinnairOrder> {
    return this.dapiService
      .invokeResponse$(fetchOrder, this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        includeServiceCatalog: true,
      })
      .pipe(
        retryWithBackoff(MmbOrderService.NUMBER_OF_RETRIES, DEFAULT_RETRY_BACKOFF_TIME, [
          503,
          ...STATUS_CODES_FOR_RETRY,
        ]),
        catchError((err: unknown) => {
          this.logErrorToSentry(err, getDapiErrorKey(err));
          return throwError(() => err);
        }),
        map((response: DapiHttpResponse<FinnairOrder>) => response.body as FinnairOrder)
      );
  }

  getOrderByUrl(orderUrl: string | null): Observable<FinnairOrder> {
    if (isNull(orderUrl)) {
      return of(null);
    }

    const params = new HttpParams().set('includeServiceCatalog', true);

    return this.http.get<FinnairOrder>(orderUrl, { params }).pipe(
      retryWithBackoff(MmbOrderService.NUMBER_OF_RETRIES, DEFAULT_RETRY_BACKOFF_TIME, [503, ...STATUS_CODES_FOR_RETRY]),
      catchError((err: unknown) => {
        this.logErrorToSentry(err, getDapiErrorKey(err));
        return throwError(() => err);
      })
    );
  }

  private retryGet$() {
    return (o$: Observable<DapiHttpResponse<void>>) =>
      o$.pipe(
        map((res) => this.locationFromResponse(res)),
        retryWithBackoff(MmbOrderService.NUMBER_OF_RETRIES, DEFAULT_RETRY_BACKOFF_TIME, ORDER_STATUS_CODES_FOR_RETRY)
      );
  }
}
